import React, { memo, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import {
  BuilderContainer,
  FixedContainer,
  FixedContainerRef,
  InternalContainer,
} from 'src/presentation/components/pages/sidekick/threads/SidekickThreadEditor';
import { Button, ButtonTypes } from 'src/presentation/components/agent_desktop_components';
import ConnectorsProvider from 'src/presentation/components/pages/sidekick/threads/components/Connectors/ConnectorsProvider';
import ConnectorWrapper from 'src/presentation/components/pages/sidekick/threads/components/Connectors/ConnectorWrapper';
import ModulesContainer from 'src/presentation/components/pages/sidekick/threads/ModulesContainer';
import RelationLines from 'src/presentation/components/pages/sidekick/threads/components/ThreadBuilder/RelationLines';
import SidePanel, {
  SidePanelRef,
} from 'src/presentation/components/pages/sidekick/threads/components/SidePanel/SidePanel';
type AnonymousModuleEditorProps = {
  id: string;
  controlBarControl: ReactNode;
  hasModules: boolean;
  isAnonymousModule?: boolean;
  moduleViewControl: ReactNode;
  readOnly?: boolean;
  renderParentsPanel: (params: unknown) => void;
  isLoading?: boolean;
};

const EditorLayout = ({
  id,
  controlBarControl,
  hasModules,
  isAnonymousModule,
  moduleViewControl,
  readOnly,
  renderParentsPanel,
  isLoading = true,
}: AnonymousModuleEditorProps) => {
  const [isParentsLoading, setIsParentsLoading] = useState(isLoading);

  const scrollableRef = useRef<HTMLDivElement>(null);
  const moduleContainerRef = useRef<HTMLDivElement>(null);
  const parentsContainerRef = useRef<HTMLDivElement>(null);
  const fixedContainerRef = useRef<FixedContainerRef>(null);
  const sidePanelRef = useRef<SidePanelRef>(null);

  useEffect(() => {
    if (sidePanelRef.current && !hasModules) {
      sidePanelRef.current?.close();
      if (fixedContainerRef.current) {
        fixedContainerRef.current.recalculate();
      }
      return;
    }
    sidePanelRef.current?.open();
    fixedContainerRef.current?.recalculate();
  }, [sidePanelRef, hasModules, fixedContainerRef]);

  const computeModuleContainerTop = useCallback(() => {
    if (!parentsContainerRef.current || !moduleContainerRef?.current) {
      return;
    }

    const pRect = parentsContainerRef.current.getClientRects()[0];
    let newY = pRect.top + (isAnonymousModule ? 0 : pRect.height + 20);
    if (pRect.height === 0) {
      newY = 100;
    }
    moduleContainerRef.current.style.top = `${newY}px`;
  }, [parentsContainerRef, moduleContainerRef, isAnonymousModule]);

  useEffect(() => {
    if (!isParentsLoading) {
      setTimeout(() => computeModuleContainerTop());
    }
  }, [isParentsLoading, computeModuleContainerTop]);

  const parentItemId = `${id}-main-container`;

  return (
    <>
      <ConnectorsProvider name={id}>
        <BuilderContainer ref={scrollableRef}>
          <FixedContainer
            hasActions={true}
            moduleContainerRef={moduleContainerRef}
            ref={fixedContainerRef}
            scrollableRef={scrollableRef}
          >
            {controlBarControl}
            {renderParentsPanel({
              computeModuleContainerTop,
              fixedContainerRef,
              moduleContainerRef,
              parentItemId,
              ref: parentsContainerRef,
              setIsParentsLoading,
            })}

            {!isParentsLoading && (
              <>
                <RelationLines modulesContainerRef={fixedContainerRef} scrollableRef={scrollableRef} />
              </>
            )}

            <InternalContainer>
              <ModulesContainer ref={moduleContainerRef}>
                <ConnectorWrapper connectedPointPosition="top" id={parentItemId} type="THREAD" meta={null}>
                  {moduleViewControl}
                </ConnectorWrapper>
              </ModulesContainer>
            </InternalContainer>
          </FixedContainer>
        </BuilderContainer>
      </ConnectorsProvider>

      <SidePanel isReadOnly={!!readOnly} isAnonymousModule={!!isAnonymousModule} ref={sidePanelRef} />

      <HelpButton
        onClick={() => window.open('https://help.gladly.com/docs/sidekick', '_blank')}
        type={ButtonTypes.SECONDARY}
      >
        Help
      </HelpButton>
    </>
  );
};

const HelpButton = styled(Button)`
  bottom: 24px;
  position: fixed;
  left: 24px;
`;

export default memo(EditorLayout);
